// The run page's case table and drawer, as data (task eval_admin_runs): each
// row's markers, its outcome per repeat, the verdict reason to show, and what
// the test split hides. Pure: tested in caseTable.test.ts, so the markers a row
// shows are pinned down without a DOM.

import type { EvalCaseRow, EvalOutcome, EvalRepeatView, EvalRole } from './types';

export type CaseMarkerKey =
  | 'hidden'
  | 'flaky'
  | 'judge_unstable'
  | 'incomplete'
  | 'errored'
  | 'case_changed'
  | 'not_in_corpus'
  | 'scope_gap';

export interface CaseMarker {
  key: CaseMarkerKey;
  label: string;
  /** What the marker means, for its title. */
  title: string;
  tone: 'warn' | 'bad' | 'muted';
}

/**
 * The markers a case row carries, most important first. A test-split row says
 * it is hidden; a row whose stored definition differs from the deployed one
 * says the case changed, so its outcome is read against the case as it was.
 */
export function caseMarkers(row: EvalCaseRow): CaseMarker[] {
  const out: CaseMarker[] = [];
  if (row.redacted) {
    out.push({
      key: 'hidden',
      label: 'Test split: hidden',
      title: 'A held-out test case: its prompt, reply and reasons stay hidden until revealed.',
      tone: 'muted',
    });
  }
  if (row.errored) {
    out.push({
      key: 'errored',
      label: 'Errored',
      title: 'A repeat ended in a provider or runner error; it counts as a failure.',
      tone: 'bad',
    });
  }
  if (row.incomplete) {
    out.push({
      key: 'incomplete',
      label: 'Incomplete',
      title: 'A repeat was never graded, or was never collected.',
      tone: 'warn',
    });
  }
  if (row.flaky) {
    out.push({
      key: 'flaky',
      label: 'Flaky',
      title: 'Its repeats disagree: some passed and some failed.',
      tone: 'warn',
    });
  }
  if (row.judgeUnstable) {
    out.push({
      key: 'judge_unstable',
      label: 'Judge unstable',
      title: 'A judge graded the same reply differently when asked twice.',
      tone: 'warn',
    });
  }
  if (row.caseChanged === true) {
    out.push({
      key: 'case_changed',
      label: 'Case changed',
      title: 'The deployed case (or its moment) differs from the one this run graded.',
      tone: 'warn',
    });
  }
  if (!row.inCorpus) {
    out.push({
      key: 'not_in_corpus',
      label: 'Not in corpus',
      title: 'The deployed corpus no longer holds this case.',
      tone: 'muted',
    });
  }
  if (row.knownScopeGap) {
    out.push({
      key: 'scope_gap',
      label: 'Scope gap',
      title: 'A known scope gap: reported, not gated.',
      tone: 'muted',
    });
  }
  return out;
}

export type RepeatMark = 'pass' | 'fail' | 'error' | 'ungraded' | 'blocked';

/**
 * One repeat's outcome, as the table's per-repeat marks show it. A Watch
 * condition's repeat fails when its condition was not met or its decision
 * did not match, as the run's failures filter and compare count it.
 */
export function repeatMark(r: EvalRepeatView): RepeatMark {
  if (r.error !== null) return 'error';
  if (r.blockedVisibility) return 'blocked';
  if (!r.complete || r.passed === null) return 'ungraded';
  return r.passed && r.decisionPassed !== false ? 'pass' : 'fail';
}

const REPEAT_TEXT: Record<RepeatMark, string> = {
  pass: 'passed',
  fail: 'failed',
  error: 'errored',
  ungraded: 'not graded',
  blocked: 'could not see what it needs',
};

export function repeatMarkText(mark: RepeatMark): string {
  return REPEAT_TEXT[mark];
}

/** "2/3 passed" — the counted repeats a case's majority read. */
export function repeatSummary(row: EvalCaseRow): string {
  if (row.countedRepeats === 0) return 'not counted';
  return `${row.passedRepeats}/${row.countedRepeats} passed`;
}

const OUTCOME_TEXT: Record<EvalOutcome, string> = {
  pass: 'Pass',
  fail: 'Fail',
  not_counted: 'Not counted',
};

export function outcomeText(outcome: EvalOutcome): string {
  return OUTCOME_TEXT[outcome];
}

/**
 * A case's outcome as the run counts it: a Watch condition fails when its
 * condition's majority fails or its decision's does. This is the row the
 * failures filter returns and the case compare calls failing.
 */
export function caseOutcome(row: Pick<EvalCaseRow, 'outcome' | 'decisionOutcome'>): EvalOutcome {
  return row.outcome === 'fail' || row.decisionOutcome === 'fail' ? 'fail' : row.outcome;
}

/**
 * A Watch condition's decision, in words: "decision 1/2 matched". Null for a
 * case with no decision, which is every case but a condition.
 */
export function decisionSummary(
  row: Pick<EvalCaseRow, 'decisionOutcome' | 'repeats'>
): string | null {
  if (row.decisionOutcome === null) return null;
  const counted = row.repeats.filter((r) => r.decisionPassed !== null);
  if (counted.length === 0) return 'decision not counted';
  const matched = counted.filter((r) => r.decisionPassed === true).length;
  return `decision ${matched}/${counted.length} matched`;
}

/** A case's repeats in repeat order (the API groups them by case, not in order). */
export function sortedRepeats(row: Pick<EvalCaseRow, 'repeats'>): EvalRepeatView[] {
  return [...row.repeats].sort((a, b) => a.repeat - b.repeat);
}

/**
 * The verdict reason to show in the table: the first failing repeat's, else
 * the first repeat's. Null when there is none, or it is hidden.
 */
export function verdictReason(row: EvalCaseRow): string | null {
  if (row.redacted) return null;
  const repeats = sortedRepeats(row);
  const ordered = [
    ...repeats.filter((r) => repeatMark(r) === 'fail' || repeatMark(r) === 'error'),
    ...repeats,
  ];
  for (const r of ordered) {
    if (r.error !== null) return r.error;
    for (const v of r.verdicts) {
      if (v.grading === 1 && v.reason !== null && v.reason.length > 0) return v.reason;
    }
  }
  return null;
}

/** A row's identity: one per model, role and case. */
export function caseRowKey(row: { model: string; role: EvalRole; caseId: string }): string {
  return `${row.model}\u0000${row.role}\u0000${row.caseId}`;
}

/** A stable test id for a row, readable in a Playwright locator. */
export function caseRowTestId(row: { model: string; role: EvalRole; caseId: string }): string {
  return `eval-case-${row.role}-${row.model}-${row.caseId}`.replace(/[^A-Za-z0-9_.:-]/g, '_');
}

/**
 * A row's split as the table shows it: train or test, "demo" for a METS demo
 * case, which is in neither split and never hidden (ADR 0314, TOM-440), and
 * null for a short-suite case, which is not split (a dash).
 */
export function splitLabel(row: Pick<EvalCaseRow, 'split' | 'splitSource'>): string | null {
  if (row.split !== null) return row.split;
  return row.splitSource === 'demo' ? 'demo' : null;
}

/**
 * What the drawer can show of a case without a reveal. A test-split row shows
 * its aggregates only; every other row shows everything the store holds.
 */
export function drawerVisibility(row: Pick<EvalCaseRow, 'redacted'>): 'full' | 'hidden' {
  return row.redacted ? 'hidden' : 'full';
}

/** A reveal's optional reason, as the API takes it: trimmed, at most 200 characters. */
export function revealReason(input: string): string | undefined {
  const t = input.trim();
  if (t.length === 0) return undefined;
  return t.slice(0, 200);
}

/** The deployed definition's fields the drawer names; the rest go in the raw view. */
export interface DefinitionView {
  prompt: string | null;
  title: string | null;
  rubric: string | null;
  facts: string[];
  expect: string | null;
  expectedTools: string[];
  category: string | null;
  source: string | null;
  note: string | null;
  moment: string | null;
  redacted: boolean;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** Reads the drawer's fields from a deployed case definition (any of the three corpora). */
export function definitionView(def: Record<string, unknown> | null | undefined): DefinitionView {
  const d = def ?? {};
  const facts = Array.isArray(d.facts)
    ? d.facts.filter((f): f is string => typeof f === 'string')
    : [];
  const expectedTools = Array.isArray(d.expectedTools)
    ? d.expectedTools.map((t) =>
        Array.isArray(t) ? t.filter((x) => typeof x === 'string').join(' or ') : String(t)
      )
    : [];
  let expect: string | null = null;
  if (typeof d.expect === 'string') expect = d.expect;
  else if (d.expect !== null && typeof d.expect === 'object') expect = JSON.stringify(d.expect);
  const moment =
    d.momentSpec !== null && typeof d.momentSpec === 'object'
      ? (() => {
          const m = d.momentSpec as Record<string, unknown>;
          return [str(m.id), str(m.archetype), str(m.mode)].filter((x) => x !== null).join(' · ');
        })()
      : str(d.moment);
  return {
    prompt: str(d.prompt),
    title: str(d.title),
    rubric: str(d.rubric),
    facts,
    expect,
    expectedTools,
    category: str(d.category),
    source: str(d.source),
    note: str(d.note),
    moment: moment !== null && moment.length > 0 ? moment : null,
    redacted: d.redacted === true,
  };
}
